import React, { useMemo, useState } from 'react';
import { View, Text, Pressable, ScrollView, ActivityIndicator } from 'react-native';
import Svg, { Line } from 'react-native-svg';
import { FontAwesome } from '@expo/vector-icons';
import DateRangePicker, { DateRange } from '../components/DateRangePicker.native';
import { useHostInsightsData } from '../hooks/useHostInsightsData';
//@ts-ignore  
import DailyKwhBarChart from '../components/graphs/DailyKwhBarChart';
//@ts-ignore
import AccumulatedEarningsLineChart from '../components/graphs/AccumulatedEarningsLineChart';
import { Dropdown, MultiSelect } from 'react-native-element-dropdown';
import { dropdownStyles } from '../styles/DropdownStyles';
import { useRoute } from '@react-navigation/native';
import { HeaderWithMenu } from '../components/menu';

type RouteParams = {
  preselectedChargerIds?: string[];
};

const HostInsights: React.FC = () => {
  const route = useRoute<any>();
  const incoming: RouteParams | undefined = route?.params;
  const [pickerVisible, setPickerVisible] = useState(false);

  const {
    data,
    chargers,
    loading,
    selectedStartDate,
    selectedEndDate,
    selectedChargerIds,
    setSelectedStartDate,
    setSelectedEndDate,
    setSelectedChargerIds,
  } = useHostInsightsData({
    chargerIds: incoming?.preselectedChargerIds,
  });

  const chargerOptions = useMemo(() => {
    const opts = chargers.map(c => ({ label: c.name || 'Unnamed', value: c.uuid as string }));
    return [{ label: 'All Chargers', value: '__ALL__' }, ...opts];
  }, [chargers]);

  const selectedValues = selectedChargerIds || [];
  const selectedCount = selectedValues.length;

  const formatLocalShort = (iso: string): string => {
    try {
      if (!iso) return '';
      const [y, m, d] = iso.split('-').map((v) => Number(v));
      const dt = new Date(y, (m || 1) - 1, d || 1);
      return dt.toLocaleDateString(undefined, { year: '2-digit', month: '2-digit', day: '2-digit' });
    } catch {
      return iso;
    }
  };

  const lifetimeKwh = Number(data.lifetimeSums?.allKwh || 0);
  const useMWh = lifetimeKwh >= 10000;
  const energyDisplay = useMWh
    ? Math.round(lifetimeKwh / 1000)
    : Math.round(lifetimeKwh);
  const energyUnit = useMWh ? 'MWh' : 'kWh';

  const onConfirmDates = (range: DateRange) => {
    if (range.startDate && range.endDate) {
      setSelectedStartDate(range.startDate);
      setSelectedEndDate(range.endDate);
    }
  };

  const handleMultiSelectChange = (vals: string[]) => {
    if (!vals) return;
    if (vals.includes('__ALL__')) {
      // select all chargers (exclude the special option)
      const allIds = chargers.map(c => c.uuid).filter(Boolean) as string[];
      setSelectedChargerIds(allIds);
    } else {
      setSelectedChargerIds(vals);
    }
  };

  const handleRemoveCharger = (chargerIdToRemove: string) => {
    if (!selectedValues || !Array.isArray(selectedValues)) return;
    const updatedIds = selectedValues.filter(id => id !== chargerIdToRemove);
    setSelectedChargerIds(updatedIds.length > 0 ? updatedIds : []);
  };

  const allSelected = chargers.length > 0 && selectedValues.length === chargers.length;

  // Get selected chargers with IDs and names for display
  const selectedChargerData = useMemo(() => {
    if (!selectedValues.length || !Array.isArray(selectedValues)) return [];
    return selectedValues
      .map(id => {
        const charger = chargers.find(c => c?.uuid === id);
        return charger ? { id, name: charger.name || 'Unnamed' } : null;
      })
      .filter((item): item is NonNullable<typeof item> => item != null);
  }, [selectedValues, chargers]);

  const selectedChargerNames = useMemo(() => {
    return selectedChargerData.map(c => c.name);
  }, [selectedChargerData]);

  // Determine display mode: chips for 1-5 chargers, text for 6+
  const useChipsDisplay = selectedChargerNames.length > 0 && selectedChargerNames.length <= 5;

  // Build compact text for 6+ chargers
  const chargerDisplayText = useMemo(() => {
    const count = selectedChargerNames.length;
    if (count === 0 || count <= 5) return null;
    return `${selectedChargerNames.slice(0, 2).join(', ')}, and ${count - 2} more`;
  }, [selectedChargerNames]);

  return (
    <View style={{ flex: 1, backgroundColor: '#1A1D21', paddingVertical: 20 }}>
      {/* Hamburger Menu Header */}
      <HeaderWithMenu forceHamburger title="Charger Insights" insideSafeArea />
      
      <ScrollView contentContainerStyle={{ padding: 16, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
        <Text style={{ color: '#FFFFFF', fontSize: 24, fontWeight: '600', marginBottom: 16, textAlign: 'center' }}>Host Insights</Text>

        {/* Filters Row */}
        <View style={{ marginBottom: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, justifyContent: 'space-between', maxWidth: 600, alignSelf: 'center', width: '100%' }}>
            <View style={{ flex: 1, paddingRight: 6 }}>
              <Pressable onPress={() => setPickerVisible(true)} style={{ backgroundColor: '#292D33', height: 40, paddingHorizontal: 12, borderRadius: 20, justifyContent: 'center' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', maxWidth: '100%' }}>
                  <FontAwesome name="calendar" size={16} color="#FF9500" style={{ marginRight: 8 }} />
                  <Text numberOfLines={1} ellipsizeMode="tail" style={{ color: 'white', fontSize: 13, flexShrink: 1 }}>{`${formatLocalShort(selectedStartDate)} to ${formatLocalShort(selectedEndDate)}`}</Text>
                </View>
              </Pressable>
            </View>
            <DateRangePicker
              visible={pickerVisible}
              onClose={() => setPickerVisible(false)}
              onConfirm={onConfirmDates}
              initialRange={{ startDate: selectedStartDate, endDate: selectedEndDate }}
            />
            {/* Charger multi-select with compact label */}
            <View style={{ position: 'relative', flex: 1, paddingLeft: 6, paddingRight: 0 }}>
              <MultiSelect
                data={chargerOptions}
                value={allSelected ? (chargers.map(c => c.uuid).filter(Boolean) as string[]) : selectedValues}
                labelField="label"
                valueField="value"
                placeholder=""
                searchPlaceholder="Search"
                onChange={handleMultiSelectChange}
                style={[dropdownStyles.dropdown, { backgroundColor: '#FFFFFF', borderRadius: 20, height: 40, paddingVertical: 8, paddingHorizontal: 12, width: '100%', borderBottomWidth: 0, borderWidth: 0, borderColor: 'transparent' }]}
                placeholderStyle={[dropdownStyles.placeholderStyle, { fontSize: 13, color: 'transparent' }]}
                selectedTextStyle={[dropdownStyles.selectedTextStyle, { fontSize: 13, color: 'transparent' }]}
                inputSearchStyle={[dropdownStyles.inputSearchStyle, { borderWidth: 1, borderColor: '#636363', borderRadius: 8, paddingHorizontal: 12, color: '#1A1D21' }]}
                renderItem={(item: any, selected?: boolean) => {
                  return (
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, paddingHorizontal: 14 }}>
                      <Text style={{ color: '#1A1D21', fontSize: 14 }}>{item?.label}</Text>
                      <Text style={{ color: selected ? '#FF9500' : 'transparent', fontSize: 18, fontWeight: '700' }}>✓</Text>
                    </View>
                  );
                }}
                renderSelectedItem={() => <View style={{ width: 0, height: 0 }} />}
                selectedStyle={{ display: 'none', height: 0, width: 0, padding: 0, margin: 0 }}
                itemContainerStyle={{ borderRadius: 12, overflow: 'hidden', marginHorizontal: 6 }}
                containerStyle={{ marginTop: 5, marginLeft: 0, borderRadius: 20, overflow: 'hidden' }}
                visibleSelectedItem={false}
              />
              <View style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, justifyContent: 'center', alignItems: 'center', pointerEvents: 'none' }}>
                <Text style={{ color: '#1A1D21', fontSize: 13, fontWeight: '500' }}>{`Filter Chargers${selectedCount > 0 ? ` (${selectedCount})` : ''}`}</Text>
              </View>
            </View>
          </View>
          <View style={{ width: '100%', height: 10 }} />
          <View style={{ width: '100%', height: 2 }}>
            <Svg width="100%" height={2}>
              <Line x1="0" y1="1" x2="100%" y2="1" stroke="#636363" strokeWidth={2} strokeDasharray="2 4" strokeLinecap="round" />
            </Svg>
          </View>
        </View>

        {/* No Chargers Selected Message */}
        {selectedChargerNames.length === 0 && (
          <View style={{ marginBottom: 16, paddingHorizontal: 12, paddingVertical: 16, backgroundColor: '#292D33', borderRadius: 12, borderWidth: 1, borderColor: '#636363' }}>
            <Text style={{ color: '#FFA500', fontSize: 14, fontWeight: '600', textAlign: 'center', marginBottom: 4 }}>
              ⚠️ No Chargers Selected
            </Text>
            <Text style={{ color: '#8D8E90', fontSize: 13, textAlign: 'center' }}>
              Please select chargers above to view insights
            </Text>
          </View>
        )}

        {/* Selected Chargers Display - Chips for 1-5, Text for 6+ */}
        {useChipsDisplay && selectedChargerNames.length > 0 && (
          <View style={{ marginBottom: 14, paddingHorizontal: 4 }}>
            <Text style={{ color: '#8D8E90', fontSize: 13, marginBottom: 8, textAlign: 'center' }}>
              {selectedChargerNames.length === 1 ? 'Viewing Charger' : `Viewing ${selectedChargerNames.length} Chargers`}
            </Text>
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={true}
              contentContainerStyle={{ 
                paddingHorizontal: 4,
                justifyContent: selectedChargerNames.length <= 2 ? 'center' : 'flex-start',
                flexGrow: selectedChargerNames.length <= 2 ? 1 : 0,
                gap: 8,
              }}
            >
              {selectedChargerData.map((chargerInfo, index) => (
                <View 
                  key={`${chargerInfo.id}-${index}`}
                  style={{ 
                    backgroundColor: '#292D33', 
                    paddingVertical: 7, 
                    paddingLeft: 14,
                    paddingRight: 10,
                    borderRadius: 18,
                    borderWidth: 1,
                    borderColor: '#3A3F47',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Text 
                    style={{ 
                      color: '#FFFFFF', 
                      fontSize: 14, 
                      fontWeight: '500',
                      maxWidth: 220,
                    }}
                    numberOfLines={1}
                  >
                    {chargerInfo.name}
                  </Text>
                  <Pressable
                    onPress={() => handleRemoveCharger(chargerInfo.id)}
                    style={({ pressed }) => ({
                      opacity: pressed ? 0.6 : 1,
                      padding: 2,
                    })}
                  >
                    <Text style={{ color: '#8D8E90', fontSize: 18, fontWeight: '600' }}>×</Text>
                  </Pressable>
                </View>
              ))}
            </ScrollView>
          </View>
        )}
        
        {chargerDisplayText && !useChipsDisplay && (
          <View style={{ marginBottom: 16, paddingHorizontal: 4 }}>
            <Text style={{ color: '#8D8E90', fontSize: 12, marginBottom: 2, textAlign: 'center' }}>
              {`Viewing ${selectedChargerNames.length} Chargers`}
            </Text>
            <Text 
              style={{ 
                color: '#FFFFFF', 
                fontSize: 14, 
                fontWeight: '500', 
                textAlign: 'center',
              }}
            >
              {chargerDisplayText}
            </Text>
          </View>
        )}

        {/* Lifetime Totals Card - Using warm orange/amber colors for host */}
        <View style={{ backgroundColor: '#292D33', borderRadius: 16, padding: 16, marginBottom: 16, minHeight: 120 }}>
          {loading ? (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 24 }}>
              <ActivityIndicator size="large" color="#FF9500" />
              <Text style={{ color: '#8D8E90', marginTop: 16, fontSize: 14 }}>Loading insights data...</Text>
            </View>
          ) : (
            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1, alignItems: 'center' }}>
                <Text style={{ color: '#FFB84D', fontSize: 18, fontWeight: '700', marginBottom: 8 }}>Lifetime Revenue</Text>
                <Text style={{ color: '#FFB84D', fontSize: 32, fontWeight: '500', fontFamily: 'RobotoCondensed-Medium' }}>
                  {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(data.lifetimeSums?.hostEarnings || 0))}
                </Text>
              </View>
              <View style={{ width: 1, backgroundColor: '#636363' }} />
              <View style={{ flex: 1, alignItems: 'center' }}>
                <Text style={{ color: '#FF9500', fontSize: 18, fontWeight: '700', marginBottom: 8 }}>Lifetime Energy</Text>
                <Text style={{ color: '#FF9500', fontSize: 32, fontWeight: '500', fontFamily: 'RobotoCondensed-Medium' }}>
                  {new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(energyDisplay)} {energyUnit}
                </Text>
              </View>
            </View>
          )}
        </View>

        {/* Charts */}
        {!loading && (
          <View style={{ gap: 16 }}>
            {/* Daily Energy Card */}
            <View style={{ backgroundColor: '#292D33', borderRadius: 16, padding: 16 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text style={{ color: '#FF9500', fontSize: 18, fontWeight: '700' }}>Daily Energy (kWh)</Text>
                <Text style={{ color: '#FF9500', fontSize: 12, fontWeight: '600' }}>{`Total: ${data.dailyKwh.reduce((s, d) => s + (Number(d.totalKwh) || 0), 0).toFixed(2)} kWh`}</Text>
              </View>
              {data.dailyKwh.reduce((s, d) => s + (Number(d.totalKwh) || 0), 0) > 0 ? (
                <View style={{ backgroundColor: '#292D33', paddingTop: 8, overflow: 'hidden' }}>
                  <DailyKwhBarChart data={data.dailyKwh} width={'100%'} barColor="#FF9500" />
                </View>
              ) : (
                <View style={{ paddingVertical: 40, alignItems: 'center' }}>
                  <Text style={{ color: '#8D8E90', fontSize: 14, fontStyle: 'italic' }}>
                    No data for the selected dates
                  </Text>
                </View>
              )}
            </View>

          {/* Revenue Card */}
          <View style={{ backgroundColor: '#292D33', borderRadius: 16, padding: 16 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text style={{ color: '#FFB84D', fontSize: 18, fontWeight: '700' }}>Accumulated Revenue</Text>
              <Text style={{ color: '#FFB84D', fontSize: 12, fontWeight: '600' }}>{`Total: $${(data.hostEarnings || []).reduce((s, d) => s + (Number((d as any)?.amount) || 0), 0).toFixed(2)}`}</Text>
            </View>
            {(data.hostEarnings || []).reduce((s, d) => s + (Number((d as any)?.amount) || 0), 0) > 0 ? (
              <View style={{ backgroundColor: '#292D33', padding: 8, overflow: 'hidden' }}>
                <AccumulatedEarningsLineChart data={(data.hostEarnings as any) || []} lineColor="#FFB84D" />
              </View>
            ) : (
              <View style={{ paddingVertical: 40, alignItems: 'center' }}>
                <Text style={{ color: '#8D8E90', fontSize: 14, fontStyle: 'italic' }}>
                  No data for the selected dates
                </Text>
              </View>
            )}
          </View>
        </View>
        )}
      </ScrollView>
    </View>
  );
};

export default HostInsights;

